import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Bar
} from 'recharts';
import { ChartBarIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';

interface OHLCData {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface CandlestickChartProps {
  symbol?: string;
  data?: OHLCData[];
  height?: number;
  showVolume?: boolean;
  timeframe?: string;
}

const CandlestickChart: React.FC<CandlestickChartProps> = ({
  symbol = 'BTC/USD',
  data,
  height = 400,
  showVolume = true,
  timeframe = '1D'
}) => {
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Generate mock OHLC data if no data provided
  const generateMockOHLCData = (): OHLCData[] => {
    const data: OHLCData[] = [];
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    
    let currentPrice = 45000; // Starting price for BTC
    
    for (let i = 0; i < 30; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      
      // Generate realistic OHLC data
      const open = currentPrice;
      const variation = (Math.random() - 0.5) * 0.1; // ±5% daily variation
      const high = open * (1 + Math.abs(variation) + Math.random() * 0.03);
      const low = open * (1 - Math.abs(variation) - Math.random() * 0.03);
      const close = low + (high - low) * Math.random();
      
      // Volume (random but realistic)
      const volume = Math.random() * 1000000 + 500000;
      
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        open: Number(open.toFixed(2)),
        high: Number(high.toFixed(2)),
        low: Number(low.toFixed(2)),
        close: Number(close.toFixed(2)),
        volume: Number(volume.toFixed(0))
      });
      
      currentPrice = close; // Next day starts with previous close
    }
    
    return data;
  };

  useEffect(() => {
    setLoading(true);
    
    // Use provided data or generate mock data
    const ohlcData = data || generateMockOHLCData();
    
    // Transform OHLC data into format suitable for Recharts
    const transformedData = ohlcData.map((item, index) => {
      const isGreen = item.close >= item.open;
      const bodyHeight = Math.abs(item.close - item.open);
      const bodyTop = Math.max(item.close, item.open);
      const bodyBottom = Math.min(item.close, item.open);
      
      return {
        ...item,
        index,
        isGreen,
        bodyHeight,
        bodyTop,
        bodyBottom,
        // For volume chart
        volumeColor: isGreen ? '#10b981' : '#ef4444',
        // For candlestick visualization using bars
        upperShadow: item.high - bodyTop,
        lowerShadow: bodyBottom - item.low,
        candleBody: bodyHeight,
        // Price change
        change: item.close - item.open,
        changePercent: ((item.close - item.open) / item.open) * 100
      };
    });
    
    setChartData(transformedData);
    setLoading(false);
  }, [data, symbol]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading {symbol} chart...</p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      if (!data) return null;
      
      const isPositive = data.change >= 0;
      
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg min-w-[200px]">
          <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Open:</span>
              <span className="font-medium">${data.open.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">High:</span>
              <span className="font-medium">${data.high.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Low:</span>
              <span className="font-medium">${data.low.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Close:</span>
              <span className="font-medium">${data.close.toLocaleString()}</span>
            </div>
            <div className="flex justify-between border-t pt-1">
              <span className="text-gray-600">Change:</span>
              <span className={`font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {isPositive ? '+' : ''}${data.change.toFixed(2)} ({data.changePercent.toFixed(2)}%)
              </span>
            </div>
            {showVolume && (
              <div className="flex justify-between">
                <span className="text-gray-600">Volume:</span>
                <span className="font-medium">{data.volume.toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom candlestick component
  const Candlestick = (props: any) => {
    const { payload, x, y, width, height } = props;
    if (!payload) return null;
    
    const data = payload;
    const candleWidth = Math.max(width * 0.6, 2);
    const centerX = x + width / 2;
    
    // Calculate positions
    const priceScale = height / (Math.max(...chartData.map(d => d.high)) - Math.min(...chartData.map(d => d.low)));
    const minPrice = Math.min(...chartData.map(d => d.low));
    
    const highY = y + height - (data.high - minPrice) * priceScale;
    const lowY = y + height - (data.low - minPrice) * priceScale;
    const openY = y + height - (data.open - minPrice) * priceScale;
    const closeY = y + height - (data.close - minPrice) * priceScale;
    
    const bodyTop = Math.min(openY, closeY);
    const bodyBottom = Math.max(openY, closeY);
    const bodyHeight = bodyBottom - bodyTop;
    
    const color = data.isGreen ? '#10b981' : '#ef4444';
    
    return (
      <g>
        {/* High-Low line */}
        <line
          x1={centerX}
          y1={highY}
          x2={centerX}
          y2={lowY}
          stroke={color}
          strokeWidth={1}
        />
        {/* Candle body */}
        <rect
          x={centerX - candleWidth / 2}
          y={bodyTop}
          width={candleWidth}
          height={Math.max(bodyHeight, 1)}
          fill={data.isGreen ? color : 'white'}
          stroke={color}
          strokeWidth={1}
        />
      </g>
    );
  };

  // Calculate price statistics
  const currentPrice = chartData[chartData.length - 1]?.close || 0;
  const previousPrice = chartData[chartData.length - 2]?.close || 0;
  const priceChange = currentPrice - previousPrice;
  const priceChangePercent = (priceChange / previousPrice) * 100;
  const isPositive = priceChange >= 0;
  
  const highestPrice = Math.max(...chartData.map(d => d.high));
  const lowestPrice = Math.min(...chartData.map(d => d.low));
  const avgVolume = chartData.reduce((sum, d) => sum + d.volume, 0) / chartData.length;

  return (
    <div className="space-y-6">
      {/* Price Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Current Price</div>
              <div className="text-2xl font-bold text-gray-900">
                ${currentPrice.toLocaleString()}
              </div>
            </div>
            {isPositive ? (
              <ArrowTrendingUpIcon className="h-8 w-8 text-green-500" />
            ) : (
              <ArrowTrendingDownIcon className="h-8 w-8 text-red-500" />
            )}
          </div>
          <div className={`text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {isPositive ? '+' : ''}${priceChange.toFixed(2)} ({priceChangePercent.toFixed(2)}%)
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-600">24H High</div>
          <div className="text-xl font-bold text-green-600">
            ${highestPrice.toLocaleString()}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-600">24H Low</div>
          <div className="text-xl font-bold text-red-600">
            ${lowestPrice.toLocaleString()}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center">
            <ChartBarIcon className="h-5 w-5 text-blue-500 mr-2" />
            <div>
              <div className="text-sm text-gray-600">Avg Volume</div>
              <div className="text-lg font-bold text-gray-900">
                {avgVolume.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Price Chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-gray-900">
            {symbol} - {timeframe} Chart
          </h4>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded mr-1"></div>
              <span>Bullish</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded mr-1"></div>
              <span>Bearish</span>
            </div>
          </div>
        </div>

        <div style={{ height: height }}>
          <ResponsiveContainer width="100%" height="70%">
            <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                stroke="#666"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                domain={['dataMin - 100', 'dataMax + 100']}
                stroke="#666"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              {/* This is a simplified approach - in production, you'd use a proper candlestick library */}
              <Bar 
                dataKey="high" 
                fill="transparent" 
                stroke="transparent"
                shape={<Candlestick />}
              />
            </ComposedChart>
          </ResponsiveContainer>

          {/* Volume Chart */}
          {showVolume && (
            <ResponsiveContainer width="100%" height="30%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#666"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  stroke="#666"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                />
                <Tooltip 
                  formatter={(value: any) => [value.toLocaleString(), 'Volume']}
                  labelFormatter={(label) => `Date: ${label}`}
                />
                <Bar dataKey="volume" fill="#e5e7eb">
                  {chartData.map((entry, index) => (
                    <Bar key={`cell-${index}`} fill={entry.volumeColor} />
                  ))}
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Chart Legend */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h5 className="text-sm font-medium text-gray-700 mb-2">Chart Guide</h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
          <div>
            <p><strong>Candlesticks:</strong> Each candle shows price movement for the selected timeframe</p>
            <p><strong>Green:</strong> Closing price higher than opening price (bullish)</p>
            <p><strong>Red:</strong> Closing price lower than opening price (bearish)</p>
          </div>
          <div>
            <p><strong>Volume:</strong> Number of shares/coins traded during the period</p>
            <p><strong>High/Low:</strong> Highest and lowest prices reached</p>
            <p><strong>Support/Resistance:</strong> Key price levels where buying/selling pressure occurs</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandlestickChart; 